import { useMemo, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { env } from '@/config/env';
import { useConfig } from '@/features/config';
import { usePlans } from './use-plans';
import {
  buildRequestedPlanIds,
  resolveVisiblePlans,
  resolveSelectedPlan,
  type PlanParams,
} from '../domain/plan-resolver';
import type { ProductPlan } from '../types';

const POPULAR_BADGE = 'MOST POPULAR';
const POPULAR_PLAN_INDEX = 1;

export interface PlanSelectionResult {
  visiblePlans: ProductPlan[];
  selectedPlan: ProductPlan | null;
  setSelectedPlan: (plan: ProductPlan) => void;
  isLoading: boolean;
}

/** The second visible plan is always the popular (pre-selected) option. */
function withPopularBadge(plans: ProductPlan[]): ProductPlan[] {
  if (plans.length <= POPULAR_PLAN_INDEX) return plans;
  return plans.map((plan, index) =>
    index === POPULAR_PLAN_INDEX ? { ...plan, badge: POPULAR_BADGE } : { ...plan, badge: undefined },
  );
}

/**
 * Resolves which plans to show on checkout and which is pre-selected.
 *
 * - `plan`/`plans` from the URL or funnel config → fetch those.
 * - Nothing specified → fetch `VITE_DEFAULT_PLAN_IDS`,
 *   so price and copy come from real plan data instead of a placeholder.
 * - No plans backend configured at all → empty list (checkout stays in loading).
 *
 * Selection is kept as an id and the plan object is derived during render, so
 * the pre-selected default updates naturally when async plans arrive — no effects.
 */
export function usePlanSelection(): PlanSelectionResult {
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>;
  const { params: configParams } = useConfig();

  // Live URL params take precedence over the funnel config.
  const planParam = (typeof search.plan === 'string' ? search.plan : undefined) ?? configParams.plan;
  const plansParam = (typeof search.plans === 'string' ? search.plans : undefined) ?? configParams.plans;

  const params: PlanParams = useMemo(
    () => ({ plan: planParam, plans: plansParam }),
    [planParam, plansParam],
  );

  const paramIds = useMemo(() => buildRequestedPlanIds(params), [params]);

  const requestedIds = useMemo(
    () => (paramIds.length > 0 ? paramIds : env.VITE_DEFAULT_PLAN_IDS),
    [paramIds],
  );

  const { plans: dynamicPlans, status, isBackendConfigured } = usePlans(requestedIds);

  const useDynamic = isBackendConfigured && requestedIds.length > 0;

  const visiblePlans = useMemo(
    () => (useDynamic ? withPopularBadge(resolveVisiblePlans(params, dynamicPlans)) : []),
    [useDynamic, params, dynamicPlans],
  );

  const defaultSelected = useMemo(
    () => resolveSelectedPlan(params, visiblePlans),
    [params, visiblePlans],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedPlan =
    visiblePlans.find((plan) => plan.id === selectedId) ?? defaultSelected ?? null;

  const isLoading =
    useDynamic && visiblePlans.length === 0 && (status === 'loading' || status === 'idle');

  return {
    visiblePlans,
    selectedPlan,
    setSelectedPlan: (plan) => setSelectedId(plan.id),
    isLoading,
  };
}
